import { useMemo } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { useSimulationStore } from '../../store/simulationStore';
import {
  getProductMixDiagnostics,
  getVisualFpsDiagnostics,
} from '../../lib/liveFrameAnimation';
import { collectProductTypeLabels } from '../../lib/validationMessages';

// ------------------------------------------------------------
// Live simulation diagnostics (product mix + frame rates)
// ------------------------------------------------------------

function CounterTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; count: number }>;
}) {
  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-surface/30 p-3 text-xs text-text-muted">
        {title}: no data yet.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-surface/30 p-3">
      <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-text-muted">
        {title}
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-text-subtle">
            <th className="pb-1 font-medium">Product type</th>
            <th className="pb-1 text-right font-medium">Count</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-t border-border-subtle/60">
              <td className="py-1 text-text">{row.label}</td>
              <td className="py-1 text-right font-mono text-text-muted">
                {row.count.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SimulationDiagnosticsPanel() {
  const document = useEditorStore((s) => s.document);
  const run = useSimulationStore((s) => s.run);
  const visualFps = useSimulationStore((s) => s.visualFps);
  const receivedVisualFps = useSimulationStore((s) => s.receivedVisualFps);
  const renderedVisualFps = useSimulationStore((s) => s.renderedVisualFps);
  const droppedSnapshotCount = useSimulationStore((s) => s.droppedSnapshotCount);

  const productLabels = useMemo(() => {
    if (!document) return new Map<string, string>();
    return collectProductTypeLabels(document);
  }, [document]);

  const mix = getProductMixDiagnostics();
  const fps = getVisualFpsDiagnostics();

  const toRows = (counts: Record<string, number>) =>
    Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([typeId, count]) => ({
        label: productLabels.get(typeId) ?? typeId,
        count,
      }));

  if (run.status === 'idle') {
    return (
      <div className="p-4 text-sm text-text-muted">
        Start a real-time simulation to view product-mix and frame diagnostics.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <section className="rounded-lg border border-border bg-surface-overlay/30 p-3 text-xs">
        <div className="mb-2 text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Frame rates
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
          <div>
            <dt className="text-text-subtle">Requested visual FPS</dt>
            <dd className="font-mono text-text">{visualFps}</dd>
          </div>
          <div>
            <dt className="text-text-subtle">Snapshot rate (recv)</dt>
            <dd className="font-mono text-text">
              {receivedVisualFps > 0 ? receivedVisualFps.toFixed(1) : fps.receivedFps.toFixed(1)}
            </dd>
          </div>
          <div>
            <dt className="text-text-subtle">Rendered FPS</dt>
            <dd className="font-mono text-text">
              {renderedVisualFps > 0 ? renderedVisualFps.toFixed(1) : fps.renderedFps.toFixed(1)}
            </dd>
          </div>
          <div>
            <dt className="text-text-subtle">Dropped snapshots</dt>
            <dd className="font-mono text-text">{droppedSnapshotCount}</dd>
          </div>
        </dl>
      </section>

      <CounterTable
        title="Generated by product type"
        rows={toRows(mix.generatedByProductType)}
      />
      <CounterTable
        title="Active by product type"
        rows={toRows(mix.activeByProductType)}
      />
      <CounterTable
        title="Streamed by product type"
        rows={toRows(mix.streamedByProductType)}
      />
      <CounterTable
        title="Rendered by product type"
        rows={toRows(mix.renderedByProductType)}
      />
    </div>
  );
}
